import { rem } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { QueryError } from 'errors'
import FileSaver from 'file-saver'
import { MRT_ColumnFiltersState, MRT_PaginationState, MRT_SortingState } from 'mantine-react-table'
import { isDeliveryCompanyDisabled } from 'modals/DispatchParcelsModal'
import { useState } from 'react'
import { Filterable } from 'types/index'
import {
  DeliveryCompany,
  DeliveryCompanyName,
  ExternalOrder,
  NewDeliveryCompany,
  NewExternalOrder,
  NewOrder,
  Order,
  OrderWithHistory,
  ShipmentResponse,
  UpdateOrder,
} from 'types/orders'

export const deliveryCompaniesQuery = queryOptions<Array<DeliveryCompany>>({
  queryKey: ['deliveryCompanies'],
  queryFn: async () => {
    const response = await axios.get('/orders/deliveryCompanies')
    return response.data
  },
  staleTime: 1000 * 60 * 5,
})

export function useDeleteDeliveryCompany() {
  const queryClient = useQueryClient()
  return useMutation<void, QueryError, DeliveryCompany>({
    mutationFn: async (deliveryCompany) => {
      await axios.delete(`/orders/deliveryCompanies/${deliveryCompany.id}`)
    },
    onSuccess: (_, deliveryCompany) => {
      queryClient.setQueriesData(
        { queryKey: ['deliveryCompanies'] },
        (oldDeliveryCompanies: Array<DeliveryCompany> | undefined) => {
          if (!oldDeliveryCompanies) return oldDeliveryCompanies
          return oldDeliveryCompanies.filter((company) => company.id !== deliveryCompany.id)
        }
      )
    },
    meta: {
      successMessage: 'Kurier został usunięty',
    },
  })
}

export function useAddDeliveryCompany() {
  const queryClient = useQueryClient()
  return useMutation<DeliveryCompany, QueryError, NewDeliveryCompany>({
    mutationFn: async (newDeliveryCompany) => {
      const response = await axios.post('/orders/deliveryCompanies', newDeliveryCompany)
      return response.data
    },
    onSuccess: (createdDeliveryCompany) => {
      queryClient.setQueriesData(
        { queryKey: ['deliveryCompanies'] },
        (oldDeliveryCompanies: Array<DeliveryCompany> | undefined) => {
          if (!oldDeliveryCompanies) return oldDeliveryCompanies
          return [createdDeliveryCompany, ...oldDeliveryCompanies]
        }
      )
    },
    meta: {
      successMessage: 'Kurier został dodany',
      closeModal: true,
    },
  })
}

export function useUpdateDeliveryCompany() {
  const queryClient = useQueryClient()
  return useMutation<DeliveryCompany, QueryError, DeliveryCompany>({
    mutationFn: async (deliveryCompany) => {
      const response = await axios.put(`/orders/deliveryCompanies/${deliveryCompany.id}`, deliveryCompany)
      return response.data
    },
    onSuccess: (updatedDeliveryCompany) => {
      queryClient.setQueriesData(
        { queryKey: ['deliveryCompanies'] },
        (oldDeliveryCompanies: Array<DeliveryCompany> | undefined) => {
          if (!oldDeliveryCompanies) return oldDeliveryCompanies
          return oldDeliveryCompanies.map((company) =>
            company.id === updatedDeliveryCompany.id ? updatedDeliveryCompany : company
          )
        }
      )
    },
    meta: {
      successMessage: 'Kurier został zaktualizowany',
      closeModal: true,
    },
  })
}

export function getOrderHistoryQuery(searchValue: string) {
  return queryOptions<Array<OrderWithHistory>, QueryError, Array<OrderWithHistory>>({
    queryKey: ['orderHistory', searchValue],
    queryFn: async () => {
      const response = await axios.get(`/orders/${searchValue}/history`)
      return response.data
    },
    select: (data) =>
      data.map((item) => ({
        order: { ...item.order, createdAt: item.order.createdAt ? new Date(item.order.createdAt) : null },
        history: item.history.map((historyItem) => ({
          ...historyItem,
          date: new Date(historyItem.date),
        })),
      })),
    enabled: searchValue.length > 0,
    staleTime: 0,
  })
}

export function useOrderHistory(searchValue: string) {
  return useQuery<Array<OrderWithHistory>, QueryError, Array<OrderWithHistory>>(getOrderHistoryQuery(searchValue))
}

export function useAddOrder() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation<Order, QueryError, NewOrder>({
    mutationFn: async (orderData) => {
      const response = await axios.post('/orders', {
        ...orderData,
        phoneNumber: orderData.phoneNumber.toString(),
        parcelLocker: orderData.delivery.parcelLocker || null,
      })
      return response.data
    },
    onSuccess: (createdOrder) => {
      queryClient.setQueriesData({ queryKey: ['orders'] }, (oldOrders: Filterable<Order> | undefined) => {
        if (!oldOrders) return oldOrders
        return {
          data: [createdOrder, ...oldOrders.data],
          total: oldOrders.total + 1,
        }
      })
      navigate({ to: '/orders', search: { orderCreated: 'true' } })
    },
    meta: {
      successMessage: 'Zamówienie zostało stworzone',
    },
  })
}

export function useAddExternalOrder() {
  return useMutation<ExternalOrder, QueryError, NewExternalOrder>({
    mutationFn: async (orderData) => {
      const response = await axios.post('/orders/external', orderData)
      return response.data
    },
    meta: {
      successMessage: 'Zewnętrzne zamówienie zostało dodane',
    },
  })
}

export function useUpdateOrder() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation<Order, QueryError, UpdateOrder>({
    mutationFn: async (orderData) => {
      const response = await axios.put(`/orders/${orderData.id}`, {
        ...orderData,
        phoneNumber: orderData.phoneNumber.toString(),
        parcelLocker: orderData.delivery.parcelLocker || null,
        dispatched: orderData.dispatched,
      })
      return response.data
    },
    onSuccess: (updatedOrder) => {
      queryClient.setQueriesData({ queryKey: ['orders'] }, (oldOrders: Filterable<Order> | undefined) => {
        if (!oldOrders) return oldOrders
        return {
          data: oldOrders.data.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)),
          total: oldOrders.total,
        }
      })
      queryClient.setQueryData(['order', updatedOrder.id], updatedOrder)
      queryClient.invalidateQueries({ queryKey: ['orderHistory'] })
      navigate({ to: '/orders', search: { orderUpdated: 'true' } })
    },
    meta: {
      successMessage: 'Zamówienie zostało zaktualizowane',
    },
  })
}

interface Params {
  globalFilter?: string
  columnFilters: MRT_ColumnFiltersState
  pagination: MRT_PaginationState
  sorting: MRT_SortingState
}

export function useOrders({ globalFilter, columnFilters, pagination, sorting }: Params) {
  const fetchUrl = new URL(`/orders`, window.location.origin)
  fetchUrl.searchParams.set('skip', `${pagination.pageIndex * pagination.pageSize}`)
  fetchUrl.searchParams.set('get', `${pagination.pageSize}`)
  fetchUrl.searchParams.set('globalFilter', globalFilter ?? '')
  let filteredColumnFilters = columnFilters.map((filter) => {
    // Adjust filter for order ID to remove "Z-" prefix
    if (filter.id === 'id' && typeof filter.value === 'string') {
      return { ...filter, value: filter.value.toLowerCase().replace('z-', '') }
    }
    return filter
  })
  filteredColumnFilters = filteredColumnFilters.filter((filter) => {
    const value = filter.value

    if (value === '' || value == null) return false

    if (typeof value === 'string') {
      return value.toLowerCase() !== 'z-'
    }

    // keep non-string values (e.g. dates)
    return true
  })
  fetchUrl.searchParams.set('filters', JSON.stringify(filteredColumnFilters ?? []))
  fetchUrl.searchParams.set('sorting', JSON.stringify(sorting ?? []))

  return useQuery<Filterable<Order>>({
    queryKey: ['orders', fetchUrl],
    queryFn: async () => {
      return (await axios.get(fetchUrl.pathname + fetchUrl.search)).data
    },
    staleTime: 1000 * 60 * 1,
  })
}

export type OrdersForShipmentData = {
  dispatchable: Array<Order>
  alreadyDispatched: Array<Order>
}

export function useOrdersForShipment(deliveryCompanyName: DeliveryCompanyName, selectedOrderIds: Array<number>) {
  return useQuery<OrdersForShipmentData, QueryError>({
    queryKey: ['ordersForShipment', deliveryCompanyName, selectedOrderIds],
    queryFn: async () => {
      const response = await axios.post(
        '/orders/forShipment',
        { orderIds: selectedOrderIds },
        { params: { deliveryCompanyName } },
      )
      return response.data
    },
    enabled: !isDeliveryCompanyDisabled(deliveryCompanyName) && selectedOrderIds.length > 0,
  })
}

export function useGenerateShipments(deliveryCompanyName: string) {
  const queryClient = useQueryClient()
  return useMutation<Array<ShipmentResponse>, QueryError, { orderIds: Array<number> }>({
    mutationKey: ['createShipments', deliveryCompanyName],
    mutationFn: async ({ orderIds }) => {
      return (await axios.post('/orders/shipment/', { orderIds }, { params: { deliveryCompanyName } })).data
    },
    onSuccess: (_, { orderIds }) => {
      queryClient.invalidateQueries({ queryKey: ['ordersForShipment'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({
        queryKey: ['order'],
        predicate: ({ queryKey }) => {
          const orderId = queryKey[1]
          return typeof orderId === 'number' && orderIds.includes(orderId)
        },
      })
    },
  })
}

export function useFetchTrackingNumbers(deliveryCompanyName: string, orderIds: Array<number> | null) {
  const queryKey = ['trackingInfo', deliveryCompanyName]
  if (orderIds) {
    queryKey.push(orderIds.join(','))
  }
  return useQuery<Array<ShipmentResponse>>({
    queryKey,
    queryFn: async () => {
      return (await axios.put('/orders/shipment/trackingInfo', { orderIds })).data
    },
    refetchInterval: ({ state: { data, error } }) => {
      if (error) {
        return false
      }
      // stop refetching when all shipments have a tracking number or an error message
      if (data && data.every((shipment) => shipment.trackingNumber || shipment.errorMessage)) {
        return false
      }
      return 3500
    },
    enabled: orderIds !== null && orderIds.length > 0,
    retry: false,
  })
}

export function getOrderQueryOptions(orderNumber: number) {
  return queryOptions<Order, QueryError, Order>({
    queryKey: ['order', orderNumber],
    queryFn: async () => {
      const response = await axios.get(`/orders/${orderNumber}`)
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useExportOrderToPdf() {
  const [id, setId] = useState<string>()

  return useMutation<Blob, QueryError, number>({
    mutationKey: ['exportOrder'],
    mutationFn: async (orderNumber) => {
      const id = notifications.show({
        position: 'bottom-center',
        color: 'blue',
        loading: true,
        title: 'Generowanie PDF',
        message: 'Trwa generowanie pliku PDF z zamówieniem. To może potrwać kilka sekund.',
        autoClose: false,
        withCloseButton: false,
      })
      setId(id)
      await new Promise((resolve) => setTimeout(resolve, 500))
      return (await axios.get(`/orders/${orderNumber}/export`, { responseType: 'blob' })).data
    },
    onSuccess: (data, orderNumber) => {
      FileSaver.saveAs(data, 'Zamówienie Z-' + orderNumber + '.pdf')
      notifications.update({
        id,
        color: 'green',
        title: 'PDF został wygenerowany',
        message: 'Plik PDF został wygenerowany i zapisany na dysku.',
        icon: <IconCheck style={{ width: rem(20), height: rem(20) }} />,
        loading: false,
        autoClose: 3000,
      })
    },
  })
}

export function useSendEmail() {
  const queryClient = useQueryClient()

  return useMutation<Blob, QueryError, number>({
    mutationKey: ['sendEmail'],
    mutationFn: async (orderNumber) => {
      return (await axios.post(`/orders/${orderNumber}/email`)).data
    },
    onSuccess: (_, orderNumber) => {
      queryClient.setQueryData<Order>(['order', orderNumber], (oldOrder) => {
        if (!oldOrder) return oldOrder
        return {
          ...oldOrder,
          orderConfirmationSentAt: new Date().toISOString(),
        }
      })
    },
    meta: {
      successMessage: 'Email został wysłany',
    },
  })
}

export function useSendShipmentEmail() {
  const queryClient = useQueryClient()

  return useMutation<void, QueryError, number>({
    mutationKey: ['sendShipmentEmail'],
    mutationFn: async (orderNumber) => {
      await axios.post(`/orders/${orderNumber}/shipment/email`)
    },
    onSuccess: (_, orderNumber) => {
      queryClient.setQueryData<Order>(['order', orderNumber], (oldOrder) => {
        if (!oldOrder) return oldOrder
        return {
          ...oldOrder,
          dispatchConfirmationSentAt: new Date().toISOString(),
        }
      })
    },
    meta: {
      successMessage: 'Email o wysyłce został wysłany',
    },
  })
}

export function useDeleteOrder() {
  const queryClient = useQueryClient()
  return useMutation<void, QueryError, number>({
    mutationFn: async (orderId) => {
      await axios.delete(`/orders/${orderId}`)
    },
    onSuccess: (_, orderId) => {
      queryClient.setQueriesData({ queryKey: ['orders'] }, (oldOrders: Filterable<Order> | undefined) => {
        if (!oldOrders) return oldOrders
        return {
          data: oldOrders.data.filter((order) => order.id !== orderId),
          total: oldOrders.total - 1,
        }
      })
    },
    meta: {
      successMessage: 'Zamówienie zostało usunięte',
    },
  })
}
